import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedCompsService } from 'avn/modules/services/shared-comps.service';
import { UserService } from 'avn/modules/services/user.service';
import { ValidatorComponent } from 'avn/modules/shared/components/validator/validator';
import { InputSafetyDirective } from 'avn/modules/shared/directives/input-safety.directive';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 25;
const SNACKBAR_DURATION_SECONDS = 5;

@Component({
  selector: 'app-activate-account',
  templateUrl: './activate-account.html',
  styleUrls: ['./activate-account.scss'],
  imports: [FormsModule, ReactiveFormsModule, NgClass, InputSafetyDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivateAccountComponent extends ValidatorComponent implements OnInit {
  public passwordForm = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(PASSWORD_MIN_LENGTH),
          Validators.maxLength(PASSWORD_MAX_LENGTH),
          this.uppercaseValidator(),
          this.lowercaseValidator(),
          this.numberValidator(),
          this.specialCharacterValidator()
        ]
      ],
      confirm_password: [
        '',
        [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH), Validators.maxLength(PASSWORD_MAX_LENGTH)]
      ]
    },
    {
      validator: this.passwordConfirming
    }
  );

  public slug!: string;
  public token!: string;
  public readonly loading = signal(false);

  public constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly api: UserService,
    private readonly shared: SharedCompsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  public checkPasswordError(errorType: string): boolean {
    return !!this.passwordForm.controls['password'].errors?.[errorType];
  }

  public passwordMaxLengthCheck(): boolean {
    if (this.passwordForm.controls['password'].value.length > PASSWORD_MAX_LENGTH) {
      return true;
    }
    return false;
  }
  public passwordMinLengthCheck(): boolean {
    if (this.passwordForm.controls['password'].value.length >= PASSWORD_MIN_LENGTH) {
      return true;
    }
    return false;
  }

  public passwordConfirming(c: AbstractControl): any {
    if (c.get('password')?.value !== c.get('confirm_password')?.value) {
      return { invalid: true };
    }
  }

  public ngOnInit(): void {
    this.route.params.subscribe((params: any) => {
      this.slug = params['slug'];
      this.token = params['token'];
      this.cdr.markForCheck();
    });
  }

  public activateAccount(): void {
    const data = this.passwordForm.value;
    this.loading.set(true);

    const responseObs = this.api.activateAccount(this.slug, this.token, data);
    responseObs.subscribe({
      next: () => {
        this.loading.set(false);
        this.shared.showSnackBar(
          'Account activated successfully. Please login to your account',
          SNACKBAR_DURATION_SECONDS
        );
        this.router.navigate(['/login']);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.shared.showSnackBar(error?.error?.message || 'Error in activating account', SNACKBAR_DURATION_SECONDS);
        this.cdr.markForCheck();
      }
    });
  }
}
